import { ExternalLink } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { Reveal, SectionHeading } from "./Reveal";
import { RECOGNITION } from "../data/siteData";

const CARD_TEST_IDS = [
  "strength-card-versatility",
  "strength-card-anticipation",
  "strength-card-speed",
];

export const Analysis = () => {
  const { lang, t } = useLanguage();

  return (
    <section id="analisis" className="scroll-mt-20 py-20 sm:py-28" data-testid="analysis-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal>
          <SectionHeading eyebrow={t.analysis.eyebrow} title={t.analysis.title} testId="analysis-heading" />
        </Reveal>

        <Reveal delay={0.05}>
          <div
            data-testid="recognition-card"
            className="mb-10 flex flex-col gap-6 border border-kit/40 bg-kit/5 p-6 sm:p-8 lg:flex-row lg:items-center lg:justify-between"
          >
            <div className="flex items-start gap-5">
              <span
                className="font-display text-6xl font-extrabold leading-none text-kit sm:text-7xl"
                data-testid="recognition-rank"
              >
                {RECOGNITION.rank}
              </span>
              <div>
                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-kit">
                  {t.analysis.recognitionLabel}
                </p>
                <p
                  className="mt-1 font-display text-2xl font-bold uppercase leading-tight text-white sm:text-3xl"
                  data-testid="recognition-title"
                >
                  {t.analysis.recognitionTitle}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-zinc-400" data-testid="recognition-source">
                  {t.analysis.recognitionSource}: {RECOGNITION.source} ({RECOGNITION.sourceDescription[lang]}) ·{" "}
                  {t.analysis.recognitionPublished}: {RECOGNITION.published}
                </p>
                <a
                  href={RECOGNITION.image}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="recognition-view-link"
                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-kit hover:underline"
                >
                  {t.analysis.recognitionView} <ExternalLink size={11} />
                </a>
              </div>
            </div>
            <div className="flex items-center gap-8">
              <div>
                <p className="font-display text-3xl font-bold text-white" data-testid="recognition-score">
                  {RECOGNITION.score}
                </p>
                <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400">
                  {t.analysis.recognitionScore}
                </p>
              </div>
              <div>
                <p className="font-display text-3xl font-bold text-white" data-testid="recognition-minutes">
                  {RECOGNITION.minutesPct[lang]}
                </p>
                <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400">
                  {t.analysis.recognitionMinutes}
                </p>
              </div>
              <a
                href={RECOGNITION.image}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="recognition-image-link"
                className="hidden shrink-0 sm:block"
                aria-label={t.analysis.recognitionView}
              >
                <img
                  src={RECOGNITION.image}
                  alt={t.analysis.recognitionImgAlt}
                  loading="lazy"
                  className="h-20 w-auto border border-ink-600 object-cover object-top transition-opacity duration-200 hover:opacity-80"
                />
              </a>
            </div>
          </div>
        </Reveal>

        <div className="grid gap-4 md:grid-cols-3">
          {t.analysis.items.map((item, i) => (
            <Reveal key={item.title} delay={0.08 * i} className="h-full">
              <article
                data-testid={CARD_TEST_IDS[i]}
                className="flex h-full flex-col border border-ink-700/60 bg-ink-900 p-6 transition-colors duration-200 hover:border-kit/50 sm:p-8"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-display text-4xl font-bold text-ink-600">
                    0{i + 1}
                  </span>
                  <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-kit">
                    {item.tag}
                  </span>
                </div>
                <h3 className="mt-5 font-display text-2xl font-bold uppercase leading-tight text-white">
                  {item.title}
                </h3>
                <div className="mt-5 space-y-4 text-sm leading-relaxed">
                  <p className="text-zinc-300">
                    <span className="mr-2 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-kit">
                      {t.analysis.factLabel}
                    </span>
                    {item.fact}
                  </p>
                  <p className="border-t border-ink-700/60 pt-4 text-zinc-400">
                    <span className="mr-2 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">
                      {t.analysis.viewLabel}
                    </span>
                    {item.view}
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
